/**
 * useDiagramFork Hook
 *
 * Provides functionality for forking diagrams from scenarios
 * and reconciling changes between original and forked diagrams.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Y from 'yjs';
import type { Scenario } from '../../../core/assistant/stores/scenariosStore';
import { loadScenariosFromYDoc } from '../../../core/assistant/stores/scenariosStore';
import type { Node, Edge } from '../../../core/models/scene';
import {
  createDiagramFork,
  computeDiagramDiff,
  createDocFromSnapshot,
  extractSceneFromYDoc,
  loadForkMetadataFromYDoc,
  reconcileDiagrams,
  type ForkDiagramResult,
} from '../services/DiagramForkService';
import {
  loadForkMetadata,
  type DiagramForkMetadata,
  type DiagramDiff,
  type ReconcileResult,
  type ReconcileOptions,
} from '../models/DiagramFork';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { uint8ArrayToBase64 } from '../../../utils/yjs';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useOrgModel } from './useOrgModel';
import { planningPresentationSnapshot } from '../services/planningPresentationSnapshot';
import { buildPlanningSeed } from '../services/buildPlanningSeed';
import type { AtlasPlanningSeed } from '../models/AtlasPlanningSeed';
import { useOrgPlanningScenarioStore } from '../stores/orgPlanningScenarioStore';
import {
  captureProtectedPlanningGeneration,
  isProtectedPlanningGenerationCurrent,
  subscribeProtectedPlanningInvalidation,
} from './protectedPlanningGeneration';

const planningBindingRequests = new Map<string, Promise<void>>();
type PlanningCreationBody = {
  name: string; update: string; stateVector: string; idempotencyKey: string; seed: AtlasPlanningSeed;
};

// =============================================================================
// Hook Interface
// =============================================================================

export interface UseDiagramForkOptions {
  /** Current diagram ID */
  diagramId: string;

  /** Current diagram name */
  diagramName: string;

  /** Current folder ID */
  folderId: string;

  /** The Yjs document for the current diagram */
  ydoc: Y.Doc | null;

  /** Current nodes in the diagram */
  nodes: Node[];

  /** Current edges in the diagram */
  edges: Edge[];
}

export interface UseDiagramForkResult {
  /** Whether this diagram is a fork of another */
  isForkDiagram: boolean;

  /** Fork metadata if this is a fork */
  forkMetadata: DiagramForkMetadata | null;

  /** Create a fork from a scenario */
  createFork: (
    scenario: Scenario,
    /** 'plan' keeps the changes as a live scenario in the new diagram (still
     *  linked, can be applied later); 'copy' bakes them into a frozen export. */
    mode?: 'plan' | 'copy',
  ) => Promise<ForkDiagramResult & { redirectUrl?: string }>;

  /** Check for changes since fork (for forked diagrams) */
  checkForChanges: () => Promise<DiagramDiff | null>;

  /** Reconcile changes with the original diagram */
  reconcile: (options: ReconcileOptions) => Promise<ReconcileResult>;

  /** Loading state */
  isLoading: boolean;

  /** Error state */
  error: string | null;
}

interface SnapshotScene {
  doc: Y.Doc;
  nodes: Node[];
  edges: Edge[];
  snapshotBase64: string;
}

interface ComparisonState {
  diff: DiagramDiff;
  original: SnapshotScene;
  base: SnapshotScene;
  fork: {
    nodes: Node[];
    edges: Edge[];
  };
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useDiagramFork(options: UseDiagramForkOptions): UseDiagramForkResult {
  const { diagramId, diagramName, folderId, ydoc, nodes, edges } = options;
  const authenticatedFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const diagramInfo = useDiagramInfo(diagramId || undefined);
  const isSourceBacked = Boolean(diagramInfo.data?.viewDefinition?.projection);
  const orgModel = useOrgModel({ folderId, diagramId, enabled: isSourceBacked });
  const planningAttempt = useRef<{ fingerprint: string; body: PlanningCreationBody } | null>(null);
  useEffect(() => {
    planningAttempt.current = null;
    return subscribeProtectedPlanningInvalidation((invalidatedFolder) => {
      if (!invalidatedFolder || invalidatedFolder === folderId) planningAttempt.current = null;
    });
  }, [folderId, diagramId]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forkMetadata, setForkMetadata] = useState<DiagramForkMetadata | null>(() =>
    loadForkMetadata(diagramId)
  );
  const [hasPersistedScenario, setHasPersistedScenario] = useState(() =>
    ydoc ? loadScenariosFromYDoc(ydoc).length > 0 : false,
  );

  useEffect(() => {
    const localFallback = loadForkMetadata(diagramId);

    if (!ydoc) {
      setForkMetadata(localFallback);
      return;
    }

    const readMetadata = () => {
      const fromDoc = loadForkMetadataFromYDoc(ydoc);
      setForkMetadata(fromDoc ?? loadForkMetadata(diagramId));
    };

    readMetadata();
    const metadataMap = ydoc.getMap('forkMetadata');
    metadataMap.observe(readMetadata);
    return () => metadataMap.unobserve(readMetadata);
  }, [diagramId, ydoc]);

  useEffect(() => {
    if (!ydoc) {
      setHasPersistedScenario(false);
      return;
    }
    const scenariosMap = ydoc.getMap('scenarios');
    const readScenarios = () => setHasPersistedScenario(loadScenariosFromYDoc(ydoc).length > 0);
    readScenarios();
    scenariosMap.observe(readScenarios);
    return () => scenariosMap.unobserve(readScenarios);
  }, [ydoc]);

  const isForkDiagram = useMemo(() => forkMetadata !== null, [forkMetadata]);

  const bindPlanningDiagram = useCallback(async (
    targetDiagramId: string,
    sourceDiagramId: string,
    targetFolderId: string,
  ): Promise<void> => {
    const requestKey = `${targetFolderId}:${targetDiagramId}`;
    const existingRequest = planningBindingRequests.get(requestKey);
    if (existingRequest) return existingRequest;

    const request = (async () => {
      const response = await authenticatedFetch(
        `/api/folders/${encodeURIComponent(targetFolderId)}/diagrams/${encodeURIComponent(targetDiagramId)}/atlas/planning-binding`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sourceDiagramId }),
        },
      );
      if (!response.ok) {
        const failure = await response.json().catch(() => null) as { error?: string } | null;
        throw new Error(failure?.error ?? `Failed to bind planning diagram (${response.status})`);
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['diagram', targetDiagramId, 'info'] }),
        queryClient.invalidateQueries({ queryKey: ['atlas-succession', targetFolderId, targetDiagramId] }),
      ]);
    })().finally(() => planningBindingRequests.delete(requestKey));

    planningBindingRequests.set(requestKey, request);
    return request;
  }, [authenticatedFetch, queryClient]);

  // Repair planning forks created before the explicit server binding existed.
  // A frozen copy has no persisted scenario and is deliberately left detached.
  useEffect(() => {
    if (!forkMetadata || !hasPersistedScenario || !folderId || !diagramId) return;
    if (diagramInfo.data?.viewDefinition?.projection === 'planning_chart') return;
    if (diagramInfo.isLoading || diagramInfo.isError) return;
    const sourceFolderId = forkMetadata.forkedFromFolderId ?? folderId;
    if (sourceFolderId !== folderId) return;

    void bindPlanningDiagram(diagramId, forkMetadata.forkedFromDiagramId, folderId)
      .catch((bindingError) => {
        setError(bindingError instanceof Error ? bindingError.message : 'Failed to bind planning diagram');
      });
  }, [
    bindPlanningDiagram,
    diagramId,
    diagramInfo.data?.viewDefinition?.projection,
    diagramInfo.isError,
    diagramInfo.isLoading,
    folderId,
    forkMetadata,
    hasPersistedScenario,
  ]);

  /**
   * Create a new diagram that is a fork of the current one with a scenario applied
   */
  const createFork = useCallback(
    async (
      scenario: Scenario,
      mode: 'plan' | 'copy' = 'copy',
    ): Promise<ForkDiagramResult & { redirectUrl?: string }> => {
      if (!ydoc) {
        return { success: false, error: 'No Yjs document available' };
      }

      setIsLoading(true);
      setError(null);

      try {
        const newDiagramName = mode === 'plan'
          ? `${scenario.name} — ${diagramName}`
          : `Copy of ${diagramName}`;
        if (!diagramInfo.data || diagramInfo.isError) throw new Error('Chart access must be resolved before copying');
        if (mode === 'copy' && (isSourceBacked || nodes.some((node) => node.lockOwner === 'projection' || node.attrs?.folderRecordId))) {
          throw new Error('Frozen copies of source-backed charts require export clearance. Use Export or Save as Planning; the original draft is unchanged.');
        }

        // Normal planning creation is one server operation. The key survives
        // a failed/unknown client outcome so Retry returns the same artifact.
        if (mode === 'plan') {
          const generation = captureProtectedPlanningGeneration(folderId);
          if (!isSourceBacked || !orgModel.data || orgModel.error) throw new Error('A current permissioned org model is required to create a plan');
          const storeState = useDiagramStore.getState();
          const sourceNodes = storeState.nodes.length > 0 ? storeState.nodes : nodes;
          const selected = useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId];
          const fingerprintBytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify({
            name: newDiagramName, delta: scenario.delta, sourceHead: selected?.headChangeSetId,
            graphVersion: orgModel.data.graphVersion, vector: uint8ArrayToBase64(Y.encodeStateVector(ydoc)),
          })));
          const fingerprint = uint8ArrayToBase64(new Uint8Array(fingerprintBytes));
          if (!isProtectedPlanningGenerationCurrent(generation)) throw new Error('Planning access changed; reload before retrying');
          if (planningAttempt.current?.fingerprint !== fingerprint) {
            planningAttempt.current = { fingerprint, body: {
              name: newDiagramName, ...planningPresentationSnapshot(ydoc),
              idempotencyKey: crypto.randomUUID(),
              seed: buildPlanningSeed(orgModel.data, sourceNodes, scenario, selected?.headChangeSetId),
            } };
          }
          const response = await authenticatedFetch(
            `/api/folders/${encodeURIComponent(folderId)}/diagrams/${encodeURIComponent(diagramId)}/atlas/planning-diagrams`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(planningAttempt.current.body),
            },
          );
          const body = await response.json().catch(() => null) as {
            diagramId?: string;
            folderId?: string;
            redirect?: string;
            error?: string;
          } | null;
          if (!response.ok || !body?.diagramId) {
            // A received rejection is definitive. Retain the key only for an
            // unknown network outcome where the server may have committed.
            if (response.status >= 400 && response.status < 500) planningAttempt.current = null;
            const message = body?.error ?? `Failed to create planning diagram (${response.status})`;
            setError(message);
            return { success: false, error: message };
          }
          planningAttempt.current = null;
          if (!isProtectedPlanningGenerationCurrent(generation)) throw new Error('Planning access changed; reopen the chart after access is refreshed');
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['diagram', body.diagramId, 'info'] }),
            queryClient.invalidateQueries({ queryKey: ['atlas-succession', body.folderId ?? folderId, body.diagramId] }),
          ]);
          return {
            success: true,
            newDiagramId: body.diagramId,
            carriedSourceScenario: Boolean(selected),
            redirectUrl: body.redirect ?? `/f/${body.folderId ?? folderId}/d/${body.diagramId}`,
          };
        }

        // Frozen copies remain on the generic diagram creation flow.
        const queryParams = new URLSearchParams({
          name: newDiagramName,
          type: 'org',
          folderId,
        });

        const createResponse = await authenticatedFetch(`/api/diagrams/new?${queryParams}`);

        if (!createResponse.ok) {
          const errorText = await createResponse.text();
          setError(`Failed to create diagram: ${errorText}`);
          return { success: false, error: `Failed to create diagram: ${errorText}` };
        }

        const createData = await createResponse.json();
        const createdDiagramId = typeof createData.diagramId === 'string' ? createData.diagramId : '';
        const createdFolderId =
          typeof createData.folderId === 'string' && createData.folderId
            ? createData.folderId
            : folderId;

        if (!createdDiagramId) {
          const errorText = 'Failed to create diagram: missing diagramId in response';
          setError(errorText);
          return { success: false, error: errorText };
        }

        // 2. Create fork content (populate with current content and apply scenario delta)
        // Read the scene at EXECUTE time, not from render-captured props: a
        // fork triggered from a surface whose render cycle predates the scene
        // load (or lags it) would otherwise snapshot an empty chart — the
        // diagram record, metadata and toast all succeed, and the content is
        // silently missing. The store is the single source of the rendered
        // scene, so it is authoritative here; the props remain a fallback for
        // callers outside the canvas store's lifecycle.
        const storeState = useDiagramStore.getState();
        const sourceNodes = storeState.nodes.length > 0 ? storeState.nodes : nodes;
        const sourceEdges = storeState.nodes.length > 0 ? storeState.edges : edges;
        const forkResult = createDiagramFork({
          sourceDiagramId: diagramId,
          sourceFolderId: folderId,
          sourceDiagramName: diagramName,
          sourceYDoc: ydoc,
          sourceNodes,
          sourceEdges,
          scenario,
          newDiagramId: createdDiagramId,
          mode,
        });

        if (!forkResult.success) {
          setError(forkResult.error || 'Failed to create fork');
          return forkResult;
        }

        // Succession and organization drafts are server-scoped by their owner
        // diagram. Stamp that trusted binding before any protected draft is
        // replayed into the new chart.
        // 3. Push forked Yjs content to the new diagram
        if (forkResult.ydocSnapshot) {
          const ingestResponse = await authenticatedFetch(`/api/diagrams/${createdDiagramId}/ingest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ update: forkResult.ydocSnapshot }),
          });

          if (!ingestResponse.ok) {
            // An empty fork that toasts success reads as data loss later.
            // Surface the failure now, while the user still has the source.
            const message = 'The new chart was created but its content could not be written. The draft stays on this chart.';
            setError(message);
            return { success: false, error: message, newDiagramId: createdDiagramId };
          }
        }

        // 4. Validate that the created diagram is readable before redirecting
        const verifyResponse = await authenticatedFetch(`/api/diagrams/${createdDiagramId}`);
        if (!verifyResponse.ok) {
          const errorText = await verifyResponse.text();
          const message = `Fork diagram created but not accessible: ${errorText}`;
          setError(message);
          return { success: false, error: message };
        }

        const redirectUrl = createData.redirect || `/f/${createdFolderId}/d/${createdDiagramId}`;

        return {
          success: true,
          newDiagramId: createdDiagramId,
          redirectUrl,
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsLoading(false);
      }
    },
    [ydoc, diagramId, diagramName, folderId, authenticatedFetch, nodes, edges, diagramInfo.data, diagramInfo.isError, isSourceBacked, orgModel.data, orgModel.error, queryClient]
  );

  const fetchSnapshotScene = useCallback(
    async (targetDiagramId: string): Promise<SnapshotScene | null> => {
      const snapshotResponse = await authenticatedFetch(`/api/diagrams/${targetDiagramId}/snapshot`);
      if (!snapshotResponse.ok) {
        const message = await snapshotResponse.text();
        if (snapshotResponse.status === 404) {
          throw new Error(
            `Original diagram could not be found (${targetDiagramId}). This fork may reference an invalid source diagram.`
          );
        }
        if (snapshotResponse.status === 403) {
          throw new Error(
            `You do not have access to the original diagram (${targetDiagramId}) needed for compare/reconcile.`
          );
        }
        throw new Error(`Failed to fetch diagram snapshot for ${targetDiagramId}: ${message}`);
      }

      const payload = await snapshotResponse.json();
      const snapshotBase64 = payload.snapshotBytesBase64 as string | null | undefined;
      if (!snapshotBase64) {
        return null;
      }

      const doc = createDocFromSnapshot(snapshotBase64);
      const scene = extractSceneFromYDoc(doc);
      return {
        doc,
        nodes: scene.nodes,
        edges: scene.edges,
        snapshotBase64,
      };
    },
    [authenticatedFetch]
  );

  const buildComparisonState = useCallback(async (): Promise<ComparisonState | null> => {
    if (!forkMetadata || !ydoc) return null;

    const original = await fetchSnapshotScene(forkMetadata.forkedFromDiagramId);
    if (!original) return null;

    const baseSnapshotBase64 = forkMetadata.forkedAtSnapshot ?? original.snapshotBase64;
    const baseDoc = createDocFromSnapshot(baseSnapshotBase64);
    const baseScene = extractSceneFromYDoc(baseDoc);

    const forkScene = extractSceneFromYDoc(ydoc);
    const diff = computeDiagramDiff({
      forkedDiagramId: diagramId,
      originalNodes: original.nodes,
      originalEdges: original.edges,
      forkedNodes: forkScene.nodes,
      forkedEdges: forkScene.edges,
      baseNodes: baseScene.nodes,
      baseEdges: baseScene.edges,
    });

    return {
      diff,
      original,
      base: {
        doc: baseDoc,
        nodes: baseScene.nodes,
        edges: baseScene.edges,
        snapshotBase64: baseSnapshotBase64,
      },
      fork: {
        nodes: forkScene.nodes,
        edges: forkScene.edges,
      },
    };
  }, [forkMetadata, ydoc, fetchSnapshotScene, diagramId]);

  /**
   * Check for changes since fork (compare current state with original and base fork snapshot)
   */
  const checkForChanges = useCallback(async (): Promise<DiagramDiff | null> => {
    if (!forkMetadata || !ydoc) {
      return null;
    }

    setIsLoading(true);
    setError(null);

    let comparison: ComparisonState | null = null;
    try {
      comparison = await buildComparisonState();
      if (!comparison) {
        setError('Failed to build comparison state');
        return null;
      }
      return comparison.diff;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      return null;
    } finally {
      comparison?.original.doc.destroy();
      comparison?.base.doc.destroy();
      setIsLoading(false);
    }
  }, [forkMetadata, ydoc, buildComparisonState]);

  /**
   * Reconcile changes with the original diagram
   */
  const reconcile = useCallback(
    async (reconcileOptions: ReconcileOptions): Promise<ReconcileResult> => {
      if (!forkMetadata || !ydoc) {
        return {
          success: false,
          appliedChanges: 0,
          skippedChanges: 0,
          unresolvedConflicts: [],
          error: 'No fork metadata or Yjs document',
        };
      }

      setIsLoading(true);
      setError(null);

      let comparison: ComparisonState | null = null;
      try {
        comparison = await buildComparisonState();
        if (!comparison) {
          return {
            success: false,
            appliedChanges: 0,
            skippedChanges: 0,
            unresolvedConflicts: [],
            error: 'Failed to compute diff',
          };
        }

        if (reconcileOptions.direction === 'pull') {
          return reconcileDiagrams({
            targetDoc: ydoc,
            diff: comparison.diff,
            options: reconcileOptions,
            sourceNodes: comparison.original.nodes,
            sourceEdges: comparison.original.edges,
          });
        }

        const pushResult = reconcileDiagrams({
          targetDoc: comparison.original.doc,
          diff: comparison.diff,
          options: reconcileOptions,
          sourceNodes: comparison.fork.nodes,
          sourceEdges: comparison.fork.edges,
        });

        if (!pushResult.success) {
          return pushResult;
        }

        const mergedOriginalUpdate = uint8ArrayToBase64(Y.encodeStateAsUpdate(comparison.original.doc));
        const ingestResponse = await authenticatedFetch(`/api/diagrams/${forkMetadata.forkedFromDiagramId}/ingest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ update: mergedOriginalUpdate }),
        });

        if (!ingestResponse.ok) {
          const message = await ingestResponse.text();
          const errorMessage = `Failed to push changes to original: ${message}`;
          setError(errorMessage);
          return {
            ...pushResult,
            success: false,
            error: errorMessage,
          };
        }

        return pushResult;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        return {
          success: false,
          appliedChanges: 0,
          skippedChanges: 0,
          unresolvedConflicts: [],
          error: errorMessage,
        };
      } finally {
        comparison?.original.doc.destroy();
        comparison?.base.doc.destroy();
        setIsLoading(false);
      }
    },
    [forkMetadata, ydoc, buildComparisonState, authenticatedFetch]
  );

  return {
    isForkDiagram,
    forkMetadata,
    createFork,
    checkForChanges,
    reconcile,
    isLoading,
    error,
  };
}

export default useDiagramFork;
